import React from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button, { buttonVariants } from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import Input, { inputVariants } from "@react/shared/ui/Input"
import Segmented from "./Segmented"
import { formatPeriod } from "../lib/dates"
import { formatHours } from "../lib/hours"

const Stat = ({ icon, value, label, tone = "text-slate-700" }) => (
  <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
    <FontAwesomeIcon icon={icon} className="text-[10.5px] text-slate-400" />
    <span className={clsx("font-semibold tabular-nums", tone)}>{value}</span>
    {label}
  </span>
)

const Toolbar = ({
  from, to, span, view, search, team, crew, crewCounts, idleOnly, idleCount, groups, stats, canWrite, hoursUrl, loading,
  onShift, onToday, onSpan, onView, onSearch, onTeam, onCrew, onIdle, onCopyWeek, onGuide,
}) => (
  <div className="px-6 pt-5 pb-3">
    <div className="flex flex-wrap items-start gap-3">
      <div className="min-w-0">
        <h1 className="text-[22px] font-bold tracking-tight text-slate-900">{I18n.t("project_planning.title")}</h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
          <Stat icon="clock" value={formatHours(stats.hours)} label={I18n.t("project_planning.stats.planned")} />
          <Stat icon="users" value={stats.people} label={I18n.t("project_planning.stats.people")} />

          {stats.overloads > 0 && (
            <Stat icon="triangle-exclamation" value={stats.overloads} label={I18n.t("project_planning.stats.overloads")} tone="text-rose-600" />
          )}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onGuide}>
          <FontAwesomeIcon icon="lightbulb" className="text-[11px] text-amber-500" />
          {I18n.t("project_planning.guide.button")}
        </Button>
        {canWrite && (
          <Button variant="secondary" size="sm" onClick={onCopyWeek}>
            <FontAwesomeIcon icon="copy" className="text-[11px]" />
            {I18n.t("project_planning.copy_week.button")}
          </Button>
        )}
        <a href={hoursUrl} className={clsx(buttonVariants({ variant: "secondary", size: "sm" }), "no-underline hover:no-underline")}>
          <FontAwesomeIcon icon="business-time" className="text-[11px]" />
          {I18n.t("project_planning.hours_link")}
        </a>
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-2.5 mt-4">
      <div className="inline-flex items-center rounded-md border border-slate-200 bg-white">
        <IconButton size="md" onClick={() => onShift(-1)} title={I18n.t("project_planning.nav.previous")}>
          <FontAwesomeIcon icon="chevron-left" />
        </IconButton>
        <button type="button" onClick={onToday} className="h-8 px-3 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50 border-x border-slate-200">
          {I18n.t("project_planning.nav.today")}
        </button>
        <IconButton size="md" onClick={() => onShift(1)} title={I18n.t("project_planning.nav.next")}>
          <FontAwesomeIcon icon="chevron-right" />
        </IconButton>
      </div>
      <span className="text-[14px] font-semibold text-slate-800 tabular-nums first-letter:uppercase min-w-[170px]">
        {formatPeriod(from, to)}
        {loading && <FontAwesomeIcon icon="circle-notch" spin className="ml-2 text-[11px] text-brand-500" />}
      </span>

      <Segmented
        value={span}
        onChange={onSpan}
        options={[
          { value: 7,  label: I18n.t("project_planning.span.week") },
          { value: 14, label: I18n.t("project_planning.span.two_weeks") },
          { value: 28, label: I18n.t("project_planning.span.four_weeks") },
        ]}
      />
      <Segmented
        value={view}
        onChange={onView}
        options={[
          { value: "people",   icon: "user",          label: I18n.t("project_planning.view.people") },
          { value: "projects", icon: "helmet-safety", label: I18n.t("project_planning.view.projects") },
        ]}
      />

      <div className="ml-auto flex items-center gap-2">
        {view === "people" && crewCounts.site_only > 0 && crewCounts.site_only < crewCounts.all && (
          <Segmented
            value={crew}
            onChange={onCrew}
            options={[
              { value: "site_only", icon: "helmet-safety", label: `${I18n.t("project_planning.filters.site_only")} ${crewCounts.site_only}` },
              { value: "all",       icon: "users",         label: `${I18n.t("project_planning.filters.everyone")} ${crewCounts.all}` },
            ]}
          />
        )}
        {view === "people" && (
          <button
            type="button"
            onClick={onIdle}
            aria-pressed={idleOnly}
            title={I18n.t("project_planning.filters.idle_hint")}
            className={clsx(
              "h-8 px-2.5 inline-flex items-center gap-1.5 rounded-md border text-[12.5px] font-medium whitespace-nowrap transition-colors",
              idleOnly ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            <FontAwesomeIcon icon="user-clock" className="text-[11px]" />
            {I18n.t("project_planning.filters.idle")}
            <span className="tabular-nums text-slate-400">{idleCount}</span>
          </button>
        )}
        {groups.length > 1 && (
          <select className={clsx(inputVariants({ size: "sm" }), "!w-48 shrink-0")} value={team} onChange={event => onTeam(event.target.value)}>
            <option value="">{I18n.t("project_planning.filters.all_teams")}</option>
            {groups.map(group => (
              <option key={group.id ?? "none"} value={group.id ?? "none"}>
                {group.name ? I18n.t("project_planning.group.team_of", { name: group.name }) : I18n.t("project_planning.group.none")}
              </option>
            ))}
          </select>
        )}
        <div className="w-56 shrink-0">
          <Input
            variant="search"
            size="sm"
            leadingIcon="magnifying-glass"
            placeholder={view === "people" ? I18n.t("project_planning.filters.search_people") : I18n.t("project_planning.filters.search_projects")}
            value={search}
            onChange={event => onSearch(event.target.value)}
          />
        </div>
      </div>
    </div>
  </div>
)

export default Toolbar
